<template>
  <v-dialog v-model="showModal" persistent width="80%">
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ t("merchant.modal.title") }}</span>
      </v-card-title>
      <v-card-text>
        <div class="d-none d-lg-flex">
          <v-row>
            <v-col sm="8" md="8">
              <v-text-field
                v-model="filter"
                :label="t('merchant.list.filter')"
                variant="outlined"
              >
                <template v-slot:prepend-inner>
                  <v-icon>mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col sm="4" md="4">
              <v-btn color="primary" @click="goToMerchantCreate"
                ><v-icon start>mdi-plus</v-icon>
                {{ t("merchant.modal.btn_create") }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <div class="d-lg-none">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="filter"
                :label="t('merchant.list.filter')"
                variant="outlined"
              >
                <template v-slot:prepend-inner>
                  <v-icon>mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn color="primary" @click="goToMerchantCreate"
                ><v-icon start>mdi-plus</v-icon>
                {{ t("merchant.modal.btn_create") }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <v-row>
          <v-col>
            <v-data-table
              :items="rows"
              :items-per-page="pager.entries_per_page"
              :items-length="pager.total_entries"
              :headers="merchantListHeaders"
              hide-default-footer
              hide-default-header
            >
              <template v-slot:item="{ item }">
                <tr @click="selectMerchant(item)" class="merchant-row">
                  <td>
                    <img
                      v-if="item.image"
                      :src="item.image.src"
                      height="25"
                      decoding="sync"
                      @error="imgError(item.uuid)"
                    />
                  </td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.brand_name }}</td>
                  <td class="d-none d-lg-table-cell">{{ item.uuid }}</td>
                </tr>
              </template>
              <template v-slot:bottom>
                <div class="text-right pa-2">
                  <span class="pa-2">{{
                    t("generic.pager_total_hits", {
                      hits: pager.total_entries,
                    })
                  }}</span>
                  <v-btn
                    v-if="prevPage"
                    @click="page = prevPage.page"
                    icon
                    size="small"
                    class="ma-2"
                    ><v-icon>mdi-chevron-left</v-icon></v-btn
                  >
                  <v-btn
                    v-if="page === 2"
                    @click="page = firstPage.page"
                    icon
                    size="small"
                    class="ma-2"
                    ><v-icon>mdi-chevron-left</v-icon></v-btn
                  >
                  <v-btn
                    v-if="nextPage"
                    @click="page = nextPage.page"
                    icon
                    size="small"
                    class="ma-2"
                    ><v-icon>mdi-chevron-right</v-icon></v-btn
                  >
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions v-if="closable">
        <v-spacer></v-spacer>
        <v-btn color="error" @click="closeModal()">
          {{ t("merchant.modal.btn_close") }} <v-icon end>mdi-close</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useMerchantStore } from '@/stores/merchant'
import { useAuthStore } from '@/stores/auth'
import { getMerchantList } from '@/api/merchant.js'

const { t } = useI18n()
const router = useRouter()
const merchantStore = useMerchantStore()
const authStore = useAuthStore()

const merchantListHeaders = [
  { key: 'image', title: '', sortable: false },
  { key: 'name', title: '', sortable: false },
  { key: 'brand_name', title: '', sortable: false },
  { key: 'uuid', title: '', sortable: false },
]

const rows = ref([])
const page = ref(1)
const pager = ref({
  links: [],
  entries_per_page: 10,
  total_entries: 0,
})
const filter = ref('')

const showModal = computed(() => merchantStore.showSelectModal)
const closable = computed(() => merchantStore.selectModalIsClosable)
const searchFilter = computed(() => merchantStore.searchFilter)

const nextPage = computed(() => pager.value.links.find((l) => l.rel == 'next'))
const prevPage = computed(() => pager.value.links.find((l) => l.rel == 'prev'))
const firstPage = computed(() => pager.value.links.find((l) => l.rel === 'first'))

watch(showModal, (val) => {
  if (val === true) {
    fetchMerchants()
  }
})

watch(page, () => {
  fetchMerchants()
})

let filterDebounce = null
watch(filter, () => {
  page.value = 1
  clearTimeout(filterDebounce)
  filterDebounce = setTimeout(() => {
    fetchMerchants()
  }, 300)
})

watch(searchFilter, (val) => {
  if (val && val !== '') {
    filter.value = searchFilter.value
  }
})

const closeModal = () => {
  merchantStore.showMerchantSelectModal({
    show: false,
    closable: false,
  })
}

const goToMerchantCreate = () => {
  merchantStore.showMerchantSelectModal({
    show: false,
    closable: false,
  })
  router.push({
    name: 'merchant-create',
  })
}

const fetchMerchants = async () => {
  const res = await getMerchantList({
    page: page.value,
    filter: filter.value,
    image_format: 'merchant-small',
  })
  if (res) {
    rows.value = res.rows
    pager.value = res.pager
  }
}

const selectMerchant = (m) => {
  if (m && m.uuid) {
    filter.value = ''
    authStore.setCurrentMerchant({ merchant: m })
    merchantStore.showMerchantSelectModal(false)
  }
}

const imgError = (ident) => {
  let idx = rows.value.findIndex((e) => e.uuid == ident)
  rows.value[idx].image.src = ''
}
</script>

<style scoped>
.merchant-row {
  cursor: pointer;
}
.merchant-row:hover td {
  background-color: rgba(var(--v-theme-primary), 0.08);
}
</style>
